import { TrackCompiler } from '../track/index.ts';
import { trackNodeOrientationDegrees, type CoasterProject } from '../schema/index.ts';
import { compileProjectTrackResult } from '../app/ride-track.ts';
import { clearanceAtDistance, validateTrackClearance } from '../validation/clearance.ts';
import { PHASE_2_MAX_TRACK_HEIGHT_M, validateTrackTopology } from './topology.ts';

export type BuildIssueSeverity = 'warning' | 'blocker';

export interface BuildIssue {
  readonly id: string;
  readonly code: string;
  readonly severity: BuildIssueSeverity;
  readonly message: string;
  readonly nodeId?: string;
  readonly sectionId?: string;
  readonly distanceM?: number;
  readonly relatedSectionIds?: readonly string[];
  readonly relatedDistancesM?: readonly number[];
  readonly metric?: { readonly value: number; readonly unit: string; readonly threshold: number };
}

export interface ValidationSample {
  readonly distanceM: number;
  readonly speedMps: number;
  readonly verticalG: number;
  readonly lateralG: number;
  readonly curvature: number;
  readonly clearanceM: number;
}

export interface ValidationProfile {
  readonly revision: number;
  readonly issues: readonly BuildIssue[];
  readonly samples: readonly ValidationSample[];
  readonly blockerCount: number;
  readonly warningCount: number;
  readonly testAllowed: boolean;
  readonly summary: string;
}

export type CandidatePreflightSeverity = 'clear' | 'warning' | 'blocker';

export interface CandidatePreflight {
  readonly severity: CandidatePreflightSeverity;
  readonly canAccept: boolean;
  readonly issues: readonly BuildIssue[];
  readonly minPlotMarginM: number;
}

export interface ProjectPlotBounds {
  readonly minX: number;
  readonly maxX: number;
  readonly minZ: number;
  readonly maxZ: number;
  readonly minY: number;
  readonly maxY: number;
}

export function projectPlotBounds(project: CoasterProject): ProjectPlotBounds {
  return {
    minX: project.plot.origin.x - project.plot.sizeMeters.x * 0.5,
    maxX: project.plot.origin.x + project.plot.sizeMeters.x * 0.5,
    minZ: project.plot.origin.z - project.plot.sizeMeters.z * 0.5,
    maxZ: project.plot.origin.z + project.plot.sizeMeters.z * 0.5,
    minY: project.plot.groundElevationMeters + 1,
    maxY: PHASE_2_MAX_TRACK_HEIGHT_M,
  };
}

function plotIssue(
  project: CoasterProject,
  node: CoasterProject['track']['nodes'][number],
): BuildIssue | null {
  const bounds = projectPlotBounds(project);
  const violations = [
    { amount: bounds.minX - node.position.x, edge: 'west' },
    { amount: node.position.x - bounds.maxX, edge: 'east' },
    { amount: bounds.minZ - node.position.z, edge: 'south' },
    { amount: node.position.z - bounds.maxZ, edge: 'north' },
  ].filter((candidate) => candidate.amount > 0);
  if (!violations.length) return null;
  const worst = violations.sort((a, b) => b.amount - a.amount)[0]!;
  return issue(
    'plot_bounds',
    'blocker',
    `This track leaves the ${worst.edge} edge by ${worst.amount.toFixed(1)} m.`,
    {
      nodeId: node.id,
      metric: { value: worst.amount, unit: 'm', threshold: 0 },
    },
  );
}

function issue(
  code: string,
  severity: BuildIssueSeverity,
  message: string,
  options: Partial<Omit<BuildIssue, 'id' | 'code' | 'severity' | 'message'>> = {},
): BuildIssue {
  return {
    id: `check:${code}:${options.nodeId ?? options.sectionId ?? 'project'}`,
    code,
    severity,
    message,
    ...options,
  };
}

function dot(
  a: { x: number; y: number; z: number },
  b: { x: number; y: number; z: number },
): number {
  return a.x * b.x + a.y * b.y + a.z * b.z;
}

export function buildValidationProfile(
  project: CoasterProject,
  revision: number,
): ValidationProfile {
  const pieceLabelBySection = new Map<string, string>();
  for (const piece of project.editor.pieceGroups) {
    const label = piece.definitionId
      .replaceAll('_', ' ')
      .replace(/\b\w/g, (character) => character.toUpperCase());
    for (const sectionId of piece.sectionIds) pieceLabelBySection.set(sectionId, label);
  }
  const topology = validateTrackTopology(project);
  const issues: BuildIssue[] = topology.issues.map((topologyIssue) =>
    issue(topologyIssue.code, topologyIssue.severity, topologyIssue.message, {
      ...(topologyIssue.nodeId ? { nodeId: topologyIssue.nodeId } : {}),
      ...(topologyIssue.sectionId ? { sectionId: topologyIssue.sectionId } : {}),
    }),
  );
  for (const node of project.track.nodes) {
    const bounds = projectPlotBounds(project);
    if (node.position.y < bounds.minY)
      issues.push(
        issue('ground', 'blocker', 'A track point is below the safe ground height.', {
          nodeId: node.id,
          metric: { value: node.position.y, unit: 'm', threshold: bounds.minY },
        }),
      );
    if (node.position.y > bounds.maxY)
      issues.push(
        issue('height_limit', 'blocker', 'This track is above the 65 m editor height limit.', {
          nodeId: node.id,
          metric: { value: node.position.y, unit: 'm', threshold: bounds.maxY },
        }),
      );
    const outside = plotIssue(project, node);
    if (outside) issues.push(outside);
  }
  for (let index = 1; index < project.track.nodes.length; index += 1) {
    const previous = project.track.nodes[index - 1]!;
    const current = project.track.nodes[index]!;
    const rollDelta = Math.abs(
      trackNodeOrientationDegrees(current) - trackNodeOrientationDegrees(previous),
    );
    if (rollDelta > 540)
      issues.push(
        issue('roll_rate', 'blocker', 'The banking changes too quickly between these points.', {
          nodeId: current.id,
          metric: { value: rollDelta, unit: 'deg', threshold: 540 },
        }),
      );
    else if (rollDelta > 240)
      issues.push(
        issue('roll_rate', 'warning', 'This roll may feel abrupt.', {
          nodeId: current.id,
          metric: { value: rollDelta, unit: 'deg', threshold: 240 },
        }),
      );
  }

  const samples: ValidationSample[] = [];
  if (project.track.nodes.length >= 4 && topology.status !== 'invalid') {
    try {
      const sampler = compileProjectTrackResult(project, new TrackCompiler()).sampler;
      const clearance = validateTrackClearance(project, sampler);
      const count = Math.max(80, Math.min(240, Math.ceil(sampler.length / 1.5)));
      const startHeight = sampler.sampleByDistance(0).position.y;
      let predictedSpeedMps = 2;
      let previousHeight = startHeight;
      let previousDistance = 0;
      let stalled = false;
      for (let index = 0; index < count; index += 1) {
        const distanceM = (index / Math.max(1, count - 1)) * sampler.length;
        const current = sampler.sampleByDistance(distanceM);
        const before = sampler.sampleByDistance(distanceM - 0.75);
        const after = sampler.sampleByDistance(distanceM + 0.75);
        const tangentDot = Math.max(-1, Math.min(1, dot(before.tangent, after.tangent)));
        const turnRadians = Math.acos(tangentDot);
        const curvature = turnRadians / 1.5;
        const curvatureVector = {
          x: (after.tangent.x - before.tangent.x) / 1.5,
          y: (after.tangent.y - before.tangent.y) / 1.5,
          z: (after.tangent.z - before.tangent.z) / 1.5,
        };
        const sectionType = current.section?.type ?? 'normal';
        const deltaDistance = Math.max(0, distanceM - previousDistance);
        const gravityAdjusted = Math.sqrt(
          Math.max(
            0,
            predictedSpeedMps * predictedSpeedMps -
              2 * 9.80665 * (current.position.y - previousHeight),
          ),
        );
        predictedSpeedMps = gravityAdjusted * Math.exp(-deltaDistance * 0.0018);
        const targetSpeed =
          typeof current.section?.parameters?.targetSpeed === 'number'
            ? current.section.parameters.targetSpeed
            : sectionType === 'lift'
              ? 5
              : sectionType === 'launch'
                ? 20
                : null;
        if (targetSpeed !== null && (sectionType === 'lift' || sectionType === 'launch'))
          predictedSpeedMps = Math.max(predictedSpeedMps, targetSpeed);
        if (targetSpeed !== null && sectionType === 'brake')
          predictedSpeedMps = Math.min(predictedSpeedMps, targetSpeed);
        const speedMps = predictedSpeedMps;
        previousHeight = current.position.y;
        previousDistance = distanceM;
        // Split centripetal acceleration along the sampled track frame. The former
        // magnitude-only estimate treated every upright planar turn as vertical G,
        // which made a fast, perfectly flat circuit fail its Ride Check.
        const verticalCurvature = dot(curvatureVector, current.up);
        const lateralCurvature = dot(curvatureVector, current.right);
        const verticalG = 1 + (speedMps * speedMps * verticalCurvature) / 9.80665;
        const lateralG = (speedMps * speedMps * lateralCurvature) / 9.80665;
        const clearanceM = clearanceAtDistance(clearance.conflicts, distanceM);
        samples.push({ distanceM, speedMps, verticalG, lateralG, curvature, clearanceM });
        if (!stalled && speedMps < 0.45 && sectionType === 'normal') {
          stalled = true;
          const lookAheadHeights = Array.from(
            { length: 12 },
            (_, lookAheadIndex) =>
              sampler.sampleByDistance(distanceM + (lookAheadIndex + 1) * 2).position.y,
          );
          const requiredRise = Math.max(0, ...lookAheadHeights) - current.position.y;
          const availableRise = (speedMps * speedMps) / (2 * 9.80665);
          const heightShortfall = Math.max(0, requiredRise - availableRise);
          const pieceLabel = current.section?.id
            ? (pieceLabelBySection.get(current.section.id) ?? 'This piece')
            : 'This piece';
          issues.push(
            issue(
              'stall',
              'warning',
              `${pieceLabel} may stall at ${distanceM.toFixed(0)} m: predicted ${speedMps.toFixed(
                1,
              )} m/s${heightShortfall > 0.05 ? `, about ${heightShortfall.toFixed(1)} m short of the next crest` : ''}. Add lift/launch speed or lower the following track.`,
              {
                distanceM,
                sectionId: current.section?.id,
                metric: { value: speedMps, unit: 'm/s', threshold: 0.45 },
              },
            ),
          );
        }
      }
      const maxSpeed = Math.max(0, ...samples.map((sample) => sample.speedMps));
      const maxVertical = Math.max(1, ...samples.map((sample) => Math.abs(sample.verticalG)));
      const maxLateral = Math.max(0, ...samples.map((sample) => Math.abs(sample.lateralG)));
      if (maxSpeed > 100)
        issues.push(
          issue(
            'speed_ceiling',
            'warning',
            'Estimated speed is far beyond a typical steel coaster.',
            { metric: { value: maxSpeed, unit: 'm/s', threshold: 100 } },
          ),
        );
      else if (maxSpeed > 55)
        issues.push(
          issue('speed_warning', 'warning', 'This coaster has a very high estimated top speed.', {
            metric: { value: maxSpeed, unit: 'm/s', threshold: 55 },
          }),
        );
      if (maxVertical > 8)
        issues.push(
          issue('vertical_g', 'warning', 'Estimated vertical forces are extreme.', {
            metric: { value: maxVertical, unit: 'G', threshold: 8 },
          }),
        );
      else if (maxVertical > 5)
        issues.push(
          issue('vertical_g', 'warning', 'Estimated vertical forces may be uncomfortable.', {
            metric: { value: maxVertical, unit: 'G', threshold: 5 },
          }),
        );
      if (maxLateral > 4)
        issues.push(
          issue('lateral_g', 'warning', 'Estimated lateral forces are extreme.', {
            metric: { value: maxLateral, unit: 'G', threshold: 4 },
          }),
        );
      else if (maxLateral > 2)
        issues.push(
          issue('lateral_g', 'warning', 'Estimated lateral forces may feel rough.', {
            metric: { value: maxLateral, unit: 'G', threshold: 2 },
          }),
        );
      for (const conflict of clearance.conflicts) {
        const message =
          conflict.severity === 'blocker'
            ? `The production train envelopes overlap by ${Math.abs(conflict.measuredClearanceM).toFixed(2)} m on these two track sections.`
            : `Train clearance is tight between these two track sections (${conflict.measuredClearanceM.toFixed(2)} m).`;
        issues.push({
          id: `check:clearance:${conflict.id}`,
          code: 'clearance',
          severity: conflict.severity,
          message,
          sectionId: conflict.sectionAId,
          distanceM: conflict.distanceAM,
          relatedSectionIds: [conflict.sectionBId],
          relatedDistancesM: [conflict.distanceBM],
          metric: {
            value: conflict.measuredClearanceM,
            unit: 'm',
            threshold: conflict.requiredClearanceM,
          },
        });
      }
    } catch {
      issues.push(
        issue('compile', 'blocker', 'This track cannot be compiled into one continuous ride.'),
      );
    }
  }

  const blockerCount = issues.filter((candidate) => candidate.severity === 'blocker').length;
  const warningCount = issues.filter((candidate) => candidate.severity === 'warning').length;
  return {
    revision,
    issues,
    samples,
    blockerCount,
    warningCount,
    testAllowed: topology.rideable && blockerCount === 0,
    summary:
      topology.status === 'incomplete'
        ? project.track.nodes.length === 0
          ? 'Place a station to start building'
          : 'Add more track before testing'
        : topology.status === 'open'
          ? 'Track is open — finish it before testing'
          : topology.status === 'invalid'
            ? 'Track order needs repair'
            : blockerCount
              ? `${blockerCount} blocker${blockerCount === 1 ? '' : 's'} to fix`
              : warningCount
                ? `Ready to test with ${warningCount} warning${warningCount === 1 ? '' : 's'}`
                : 'Ready to test',
  };
}

const CANDIDATE_RELEVANT_CODES = new Set([
  'section_count',
  'section_order',
  'node_limit',
  'plot_bounds',
  'ground',
  'height_limit',
  'compile',
  'roll_rate',
  'clearance',
]);

function issueCounts(issues: readonly BuildIssue[]): ReadonlyMap<string, number> {
  const counts = new Map<string, number>();
  for (const candidate of issues) counts.set(candidate.code, (counts.get(candidate.code) ?? 0) + 1);
  return counts;
}

export function assessCandidateProject(
  base: CoasterProject,
  candidate: CoasterProject,
  affectedNodeIds: readonly string[],
  affectedSectionIds: readonly string[],
): CandidatePreflight {
  const affectedNodes = new Set(affectedNodeIds);
  const affectedSections = new Set(affectedSectionIds);
  const baseline = buildValidationProfile(base, 0);
  const profile = buildValidationProfile(candidate, 1);
  const baselineCounts = issueCounts(baseline.issues);
  const seenCounts = new Map<string, number>();
  const issues = profile.issues.filter((candidateIssue) => {
    if (!CANDIDATE_RELEVANT_CODES.has(candidateIssue.code)) return false;
    const nextCount = (seenCounts.get(candidateIssue.code) ?? 0) + 1;
    seenCounts.set(candidateIssue.code, nextCount);
    const touchesCandidate =
      (candidateIssue.nodeId ? affectedNodes.has(candidateIssue.nodeId) : false) ||
      (candidateIssue.sectionId ? affectedSections.has(candidateIssue.sectionId) : false);
    const newlyIntroduced = nextCount > (baselineCounts.get(candidateIssue.code) ?? 0);
    return touchesCandidate || newlyIntroduced;
  });
  const bounds = projectPlotBounds(candidate);
  const candidateNodes = candidate.track.nodes.filter((node) => affectedNodes.has(node.id));
  const minPlotMarginM = candidateNodes.length
    ? Math.min(
        ...candidateNodes.map((node) =>
          Math.min(
            node.position.x - bounds.minX,
            bounds.maxX - node.position.x,
            node.position.z - bounds.minZ,
            bounds.maxZ - node.position.z,
          ),
        ),
      )
    : Number.POSITIVE_INFINITY;
  const blocker = issues.some((candidateIssue) => candidateIssue.severity === 'blocker');
  const warning = issues.some((candidateIssue) => candidateIssue.severity === 'warning');
  return {
    severity: blocker ? 'blocker' : warning ? 'warning' : 'clear',
    canAccept: !blocker,
    issues,
    minPlotMarginM,
  };
}

export class ValidationCoordinator {
  private request = 0;

  async analyze(project: CoasterProject, revision: number): Promise<ValidationProfile | null> {
    const request = ++this.request;
    await new Promise<void>((resolve) => setTimeout(resolve, 0));
    const profile = buildValidationProfile(project, revision);
    return request === this.request ? profile : null;
  }

  cancel(): void {
    this.request += 1;
  }
}
